import React, { useEffect, useState } from 'react';

import ContainerFlex from '~/components/containerFlex';
import { IOption } from '~/components/cartViewSelect';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';

import * as St from '../../style';
import CampaingCriteriosPesquisa from './criteriosPesquisa';
import CampaingCriteriosSegmentacao from './criteriosSegmentacao';
import CampaingCriteriosFiltro, {
  ICamposCriteriosFiltro,
} from './criteriosFiltros';

export interface IAutomaticTabData {
  baseDados: string;
  selected: IOption[];
  formsCriteriosFiltros: ICamposCriteriosFiltro;
}

export const getInitialAutomaticTabData = (): IAutomaticTabData => ({
  baseDados: 'BOX_BASE_CLIENTES',
  selected: [],
  formsCriteriosFiltros: {
    ufOptions: [],
    ufSelects: [],
    cidadesOptions: [],
    cidadesSelects: [],
    bairrosOptions: [],
    bairrosSelects: [],
    statusSelects: [],
    isLoading: false,
    limitMinDisponivel: '',
    limitMaxDisponivel: '',
  },
});

interface IAbaAutomaticaProps {
  onChange: (data: IAutomaticTabData) => void;
}

const AbaAutomatica: React.FC<IAbaAutomaticaProps> = ({ onChange }) => {
  const [formsCriteriosFiltros, setFormsCriteriosFiltros] =
    useState<ICamposCriteriosFiltro>(getInitialAutomaticTabData().formsCriteriosFiltros);
  const [baseDados, setBaseDados] = useState('BOX_BASE_CLIENTES');
  const [selected, setSelected] = useState<IOption[]>([]);
  const [filtroUF, setFiltroUF] = useState<string>('');
  const [filtroCidades, setFiltroCidades] = useState<string>('');

  const GETUF = useApi(Endpoints.boxBaseClients.getUf, ApiMethod.GET);
  const GETCidades = useApi(
    Endpoints.boxBaseClients.getCities.replace('PARAM_FILTRO', filtroUF || ''),
    ApiMethod.GET,
  );
  const GETBairros = useApi(
    Endpoints.boxBaseClients.getBairros.replace(
      'PARAM_FILTRO',
      filtroCidades + '&' + filtroUF || '',
    ),
    ApiMethod.GET,
  );

  /**
   * Busca as UFs para popular o select de estados
   */
  async function handleGetUF() {
    var aux = [];
    var index = 0;
    const response = await GETUF.callApi();

    (response.data as any[])?.forEach((item) => {
      aux.push({ id: index++, value: item, label: item });
    });
    setFormsCriteriosFiltros((prev) => ({
      ...prev,
      ufOptions: aux,
    }));
  }

  /**
   * Busca as cidades com base nas UFs selecionadas
   */
  async function handleGetCidades() {
    var aux = [];
    var index = 0;
    const response = await GETCidades.callApi();

    (response.data as any[])?.forEach((item) => {
      aux.push({ id: index++, value: item, label: item });
    });
    setFormsCriteriosFiltros((prev) => ({
      ...prev,
      cidadesOptions: aux,
    }));
  }

  /**
   * Busca os bairros com base nas UFs selecionadas
   */
  async function handleGetBairros() {
    var aux = [];
    var index = 0;
    const response = await GETBairros.callApi();

    (response.data as any[])?.forEach((item) => {
      aux.push({ id: index++, value: item, label: item });
    });
    setFormsCriteriosFiltros((prev) => ({
      ...prev,
      bairrosOptions: aux,
    }));
  }

  useEffect(() => {
    onChange({
      baseDados,
      selected,
      formsCriteriosFiltros,
    });
  }, [baseDados, selected, formsCriteriosFiltros, onChange]);

  useEffect(() => {
    if (selected.length > 0) {
      if (selected.find((item) => item.value === 'BAIRRO')) {
        handleGetUF();
      }
    }
  }, [selected]);

  /**
   * Dispara a busca das cidades quando o filtro de cidades (baseado nas UFs selecionadas) for alterado
   */
  useEffect(() => {
    let filtro = '';
    formsCriteriosFiltros.ufSelects.forEach((uf, index) => {
      filtro += 'estados=' + uf.value;
      if (index < formsCriteriosFiltros.ufSelects.length - 1) {
        filtro += '&';
      }
    });
    setFiltroUF(filtro);
  }, [formsCriteriosFiltros.ufSelects]);

  useEffect(() => {
    if (filtroUF !== '') {
      handleGetCidades();
    }
  }, [filtroUF]);

  /**
   * Dispara a busca dos bairros quando o filtro de bairros (baseado nas Cidades selecionadas) for alterado
   */
  useEffect(() => {
    let filtro = '';
    formsCriteriosFiltros.cidadesSelects.forEach((cidade, index) => {
      filtro += 'cidades=' + cidade.value;
      if (index < formsCriteriosFiltros.cidadesSelects.length - 1) {
        filtro += '&';
      }
    });
    setFiltroCidades(filtro);
  }, [formsCriteriosFiltros.cidadesSelects]);

  useEffect(() => {
    if (filtroCidades !== '') {
      handleGetBairros();
    }
  }, [filtroCidades]);

  return (
    <>
      <ContainerFlex row wrap marginTop={20}>
        <St.TitleLevel>1. Critérios de pesquisa</St.TitleLevel>
      </ContainerFlex>

      <CampaingCriteriosPesquisa
        baseDados={baseDados}
        setBaseDados={setBaseDados}
      />

      <ContainerFlex row wrap marginTop={20}>
        <St.TitleLevel>2. Critérios de segmentação</St.TitleLevel>
      </ContainerFlex>

      <CampaingCriteriosSegmentacao selected={selected} setSelected={setSelected} />

      {selected.length > 0 && (
        <>
          <ContainerFlex row wrap marginTop={20}>
            <St.TitleLevel>3. Filtros</St.TitleLevel>
          </ContainerFlex>

          <CampaingCriteriosFiltro
            selected={selected}
            formsCriteriosFiltros={formsCriteriosFiltros}
            setFormsCriteriosFiltros={setFormsCriteriosFiltros}
          />
        </>
      )}
    </>
  );
};

export default AbaAutomatica;
